import { useState } from "react";
import { toast } from "sonner";
import { Loader2, KeyRound, ArrowLeft, Check } from "lucide-react";
import { api } from "../../lib/api";

const STEPS = ["Email", "Κωδικός", "Νέος κωδικός"];

const errMsg = (e) => {
  const d = e?.response?.data?.detail;
  if (typeof d === "string") return d;
  if (Array.isArray(d)) return "Ελέγξτε τα στοιχεία που συμπληρώσατε.";
  if (!e?.response) return "Δεν υπάρχει σύνδεση με τον διακομιστή. Ελέγξτε το internet και δοκιμάστε ξανά.";
  return `Σφάλμα διακομιστή (${e.response.status}). Δοκιμάστε ξανά σε λίγο.`;
};

const Stepper = ({ step }) => (
  <ol className="mt-6 flex gap-2" data-testid="setpw-stepper">
    {STEPS.map((s, i) => (
      <li key={s} className={`flex flex-1 items-center gap-2 rounded-full border px-3 py-1.5 font-mono text-[10px] uppercase tracking-[0.12em] ${i + 1 <= step ? "border-[#00E676] text-white" : "border-white/10 text-zinc-500"}`}>
        {i + 1 < step ? <Check className="h-3 w-3 text-[#00E676]" /> : <span>{i + 1}</span>}{s}
      </li>
    ))}
  </ol>
);

export const SetPassword = ({ onDone }) => {
  const [step, setStep] = useState(1);
  const [email, setEmail] = useState("");
  const [code, setCode] = useState("");
  const [pw, setPw] = useState("");
  const [pw2, setPw2] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const run = async (fn) => {
    setLoading(true);
    setError("");
    try { await fn(); } catch (e) { setError(errMsg(e)); } finally { setLoading(false); }
  };

  const submit = (e) => {
    e.preventDefault();
    if (step === 1) {
      if (!email.includes("@")) return setError("Συμπληρώστε το email διαχειριστή.");
      return run(async () => {
        await api.post("/auth/request-code", { email: email.trim() });
        setStep(2);
        toast.success("Ο κωδικός επιβεβαίωσης στάλθηκε στο email σας.");
      });
    }
    if (step === 2) {
      if (code.length !== 6) return setError("Ο κωδικός επιβεβαίωσης έχει 6 ψηφία.");
      return run(async () => {
        await api.post("/auth/verify-code", { email: email.trim(), code });
        setStep(3);
      });
    }
    if (pw.length < 10) return setError("Ο κωδικός πρέπει να έχει τουλάχιστον 10 χαρακτήρες.");
    if (pw !== pw2) return setError("Οι κωδικοί δεν ταιριάζουν.");
    return run(async () => {
      await api.post("/auth/set-password", { email: email.trim(), code, password: pw });
      toast.success("Ο νέος κωδικός αποθηκεύτηκε. Συνδεθείτε.");
      onDone(email.trim());
    });
  };

  const hint = [
    "Θα σταλεί εξαψήφιος κωδικός επιβεβαίωσης στο email του διαχειριστή.",
    `Εισάγετε τον εξαψήφιο κωδικό που λάβατε στο ${email}. Ισχύει 15 λεπτά.`,
    "Επιβεβαιώθηκε! Ορίστε τώρα τον νέο σας κωδικό πρόσβασης.",
  ][step - 1];
  const label = ["Αποστολή κωδικού", "Επιβεβαίωση", "Αποθήκευση κωδικού"][step - 1];

  return (
    <form onSubmit={submit} className="glass w-full max-w-md rounded-[26px] p-8 sm:p-10" data-testid="set-password-form" noValidate>
      <KeyRound className="h-10 w-10 text-[#00E676]" />
      <h1 className="mt-6 font-display text-2xl font-black uppercase text-white">Ορισμός κωδικού</h1>
      <Stepper step={step} />
      <p className="mt-5 text-sm leading-relaxed text-zinc-300" data-testid="setpw-hint">{hint}</p>
      <div className="mt-6 space-y-4">
        {step === 1 && <input className="input" type="email" inputMode="email" placeholder="Email διαχειριστή" value={email} onChange={(e) => setEmail(e.target.value)} autoComplete="username" data-testid="setpw-email-input" />}
        {step === 2 && <input className="input text-center text-2xl tracking-[0.5em]" inputMode="numeric" maxLength={6} placeholder="000000" value={code} onChange={(e) => setCode(e.target.value.replace(/\D/g, ""))} autoComplete="one-time-code" autoFocus data-testid="setpw-code-input" />}
        {step === 3 && (
          <>
            <input type="email" value={email} autoComplete="username" readOnly hidden />
            <input className="input" type="password" placeholder="Νέος κωδικός (τουλάχιστον 10 χαρακτήρες)" value={pw} onChange={(e) => setPw(e.target.value)} autoComplete="new-password" autoFocus data-testid="setpw-password-input" />
            <input className="input" type="password" placeholder="Επανάληψη νέου κωδικού" value={pw2} onChange={(e) => setPw2(e.target.value)} autoComplete="new-password" data-testid="setpw-password2-input" />
          </>
        )}
      </div>
      {error && <p className="mt-4 text-sm text-red-400" data-testid="setpw-error">{error}</p>}
      <button type="submit" disabled={loading} className="btn-primary btn-lg mt-6 w-full justify-center" data-testid="setpw-submit-btn">
        {loading ? <Loader2 className="h-5 w-5 animate-spin" /> : label}
      </button>
      {step === 2 && <button type="button" onClick={() => { setStep(1); setCode(""); setError(""); }} className="mt-4 w-full text-sm text-zinc-300 hover:text-white" data-testid="setpw-resend-btn">Δεν το λάβατε; Νέα αποστολή</button>}
      <button type="button" onClick={() => onDone("")} className="mt-6 flex items-center gap-2 font-mono text-xs uppercase tracking-[0.2em] text-zinc-400 hover:text-white" data-testid="setpw-back-btn"><ArrowLeft className="h-4 w-4" />Σύνδεση</button>
    </form>
  );
};
